import { UUID, Color } from '../types';
import * as THREE from 'three';
import { POIData } from '../poi/POIData';
import { SimpleSettingsProperty } from '../properties';

export type MaterialType = 'solid' | 'textured' | 'image' | 'video' | 'canvas' | 'pbr';
export type BlendMode = 'normal' | 'additive' | 'subtractive' | 'multiply';
export type Side = 'front' | 'back' | 'double';

export abstract class Material {
    public readonly id: UUID;
    public readonly name: string;
    public readonly type: MaterialType;
    
    /**
     * The aspect ratio of the material's texture. Changes when a new texture is loaded.
     * Shapes that depend on aspect ratio should listen to this property's `onChange` event.
     */
    public readonly aspectRatio: SimpleSettingsProperty<number>;
    public readonly renderBackface: SimpleSettingsProperty<boolean>;

    constructor(
        type: MaterialType,
        name: string = 'Material'
    ) {
        this.id = crypto.randomUUID();
        this.name = name;
        this.type = type;
        this.aspectRatio = new SimpleSettingsProperty('Aspect Ratio', 1.0, 1.0, 'Texture');
        this.renderBackface = new SimpleSettingsProperty('Render Backface', false, false, 'Appearance');
    }
    
    // Abstract methods to be implemented by subclasses
    abstract getThreeMaterial(): THREE.Material;
    
    /*
    abstract clone(): Material;
    */
    
    /**
     * DEPRECATED: Use the `aspectRatio` property instead.
     * This getter is kept for a short time for legacy compatibility.
     * @deprecated
     */
    public get _legacy_aspectRatio(): number {
        return this.aspectRatio.value ?? 1.0;
    }
    
    serialize(): any {
        return {
            id: this.id,
            name: this.name,
            type: this.type,
        }
    }
    
    // Static factory method will be implemented in MaterialFactory to avoid circular imports
    static create(type: MaterialType, params: any = {}): Material {
        throw new Error('Use MaterialFactory.create() instead to avoid circular imports');
    }

    public onUpdate(deltaTime: number): void {
        // Can be overridden by subclasses for animations, etc.
    }

    public dispose(): void {
        // Can be overridden by subclasses to dispose resources
    }

    // Material activation state - can be overridden by subclasses
    public setActive(): void {
        // Default implementation does nothing
        // Override in subclasses for specific behavior (e.g., VideoMaterial -> play)
    }

    public setInactive(): void {
        // Default implementation does nothing
        // Override in subclasses for specific behavior (e.g., VideoMaterial -> pause)
    }
} 